/**
 * Intercepts clicks on .js-cart-permalink links that point to Shopify cart
 * permalinks or product URLs, adds items via AJAX, and opens the cart drawer.
 */
(function () {
  const CART_PATH_RE = /\/cart\/((?:\d+:\d+)(?:,\d+:\d+)*)\/?$/;
  const CART_ADD_PATH_RE = /\/cart\/add\/?$/;
  const PRODUCT_PATH_RE = /\/products\/([^/?#]+)\/?$/;

  /**
   * Parses a cart permalink URL into an array of { id, quantity } items.
   * Supports:
   *   /cart/123:1
   *   /cart/123:1,456:2
   *   /cart/add?id=123&quantity=1
   * @param {string} href
   * @returns {Array<{id: number, quantity: number}>|null}
   */
  function parseCartPermalink(href) {
    if (!href) return null;

    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }

    const pathMatch = url.pathname.match(CART_PATH_RE);
    if (pathMatch) {
      return pathMatch[1].split(',').map((pair) => {
        const [id, quantity] = pair.split(':');
        return { id: Number(id), quantity: Number(quantity) || 1 };
      }).filter((item) => item.id > 0);
    }

    if (CART_ADD_PATH_RE.test(url.pathname)) {
      const id = Number(url.searchParams.get('id') || url.searchParams.get('variant_id'));
      const quantity = Number(url.searchParams.get('quantity')) || 1;
      if (id > 0) return [{ id, quantity }];
    }

    return null;
  }

  /**
   * Resolves items from a product page URL (first available variant).
   * @param {string} href
   * @returns {Promise<Array<{id: number, quantity: number}>|null>}
   */
  async function parseProductUrl(href) {
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }

    const match = url.pathname.match(PRODUCT_PATH_RE);
    if (!match) return null;

    const handle = decodeURIComponent(match[1]);
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return null;

    const product = await response.json();
    if (!product.variants || !product.variants.length) return null;

    const variant = product.variants.find((v) => v.available) || product.variants[0];
    return [{ id: variant.id, quantity: 1 }];
  }

  /**
   * @param {string} href
   * @returns {Promise<Array<{id: number, quantity: number}>|null>}
   */
  async function resolveItems(href) {
    const cartItems = parseCartPermalink(href);
    if (cartItems && cartItems.length) return cartItems;
    return parseProductUrl(href);
  }

  /**
   * Handles click on a cart-permalink link.
   * @param {MouseEvent} evt
   */
  async function handleClick(evt) {
    const link = evt.target.closest('.js-cart-permalink');
    if (!link) return;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) return;

    const href = link.getAttribute('href');
    if (!href || link.getAttribute('aria-disabled') === 'true') return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || theme.settings.cartType === 'page') return;

    // Peek: only intercept URLs we can resolve to cart items
    const looksLikeCartOrProduct = CART_PATH_RE.test(new URL(href, window.location.origin).pathname)
      || CART_ADD_PATH_RE.test(new URL(href, window.location.origin).pathname)
      || PRODUCT_PATH_RE.test(new URL(href, window.location.origin).pathname);

    if (!looksLikeCartOrProduct) return;

    evt.preventDefault();

    link.setAttribute('aria-disabled', 'true');
    link.classList.add('is-loading');

    try {
      const items = await resolveItems(href);
      if (!items || items.length === 0) {
        window.location.href = href;
        return;
      }

      const sections = `${cartDrawer.dataset.section},cart-icon-bubble`;

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const error = typeof data.description === 'string' ? data.description : (data.message || theme.strings.cartError);
        document.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error }
        }));
        throw new Error(error);
      }

      cartDrawer.renderContents(data, true);
    } catch (error) {
      console.log(error); // eslint-disable-line
      if (cartDrawer) cartDrawer.refresh(true);
    } finally {
      link.classList.remove('is-loading');
      link.removeAttribute('aria-disabled');
    }
  }

  document.addEventListener('click', handleClick);
})();
